import React, { FC } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import i18n from 'i18n-js';
import { scale } from '@components/frame';
import { font } from '@styles/fonts';
import ColorWrapper from '@styles/colors';
import { SmallLoading } from '@components/loading';
import { ColorFunction } from '@styles/colors/types';
import { selectUpdatingImageMetadataInProgress } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { useSelector } from 'react-redux';
import ModalWrapper from '@components/modal/ModalWrapper';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

interface RemoveAttachmentModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: () => void;
  C: ColorFunction;
}

const RemoveAttachmentModal: FC<RemoveAttachmentModalProps> = ({ visible, onClose, onConfirm, C }) => {
  const updateInProgress = useSelector(selectUpdatingImageMetadataInProgress);
 
  return (
    <ModalWrapper isVisible={visible} onBackdropPress={onClose} C={C} backdropColor="transparent" backdropOpacity={1}> 
      <View
        style={{
          flex: 1,
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: scale(40),
        }}
      >
        <View
          testID={TEST_IDS.documentsRemoveAttachmentModal.root}
          style={{
            backgroundColor: C('Background/Light/Modal'),
            borderRadius: scale(14),
            width: '100%',
            maxWidth: scale(300),
            overflow: 'hidden',
          }}
        >
          {updateInProgress && (
            <View style={{ height: scale(200), justifyContent: 'center', alignItems: 'center' }}>
              <SmallLoading />
            </View>
          )}

          {!updateInProgress && (
            <>
              {/* Header and content */}
              <View
                style={{
                  paddingHorizontal: scale(20),
                  paddingTop: scale(20),
                  paddingBottom: scale(16),
                  alignItems: 'center',
                }}
              >
                <Text
                  style={[
                    font['Headline'],
                    {
                      color: C('Label/Light/Primary'),
                      marginBottom: scale(8),
                      textAlign: 'center',
                    },
                  ]}
                >
                  {i18n.t('Remove attachment from item?')}
                </Text>
                <Text
                  style={[
                    font['Footnote'],
                    {
                      color: C('Label/Light/Secondary'),
                      textAlign: 'center',
                      lineHeight: scale(18),
                    },
                  ]}
                >
                  {i18n.t('This document will no longer be linked to the item. The document itself will remain intact.')}
                </Text>
              </View>

              {/* Separator */}
              <View
                style={{
                  height: StyleSheet.hairlineWidth,
                  backgroundColor: C('Hairline/Light/Secondary'),
                }}
              />

              {/* Remove button */}
              <TouchableOpacity
                testID={TEST_IDS.documentsRemoveAttachmentModal.confirmButton}
                onPress={() => {
                  onConfirm();
                  onClose();
                }}
                activeOpacity={0.8}
                style={{
                  paddingVertical: scale(16),
                  alignItems: 'center',
                }}
              >
                <Text
                  style={[
                    font['Body'],
                    {
                      color: C('Label/Light/Warning'),
                      textAlign: 'center',
                    },
                  ]}
                >
                  {i18n.t('Remove')}
                </Text>
              </TouchableOpacity>

              {/* Separator */}
              <View
                style={{
                  height: StyleSheet.hairlineWidth,
                  backgroundColor: C('Hairline/Light/No trans [mobile]'),
                }}
              />

              {/* Keep attachment button */}
              <TouchableOpacity
                onPress={onClose}
                activeOpacity={0.8}
                style={{
                  paddingVertical: scale(16),
                  alignItems: 'center',
                }}
              >
                <Text
                  style={[
                    font['Body'],
                    {
                      color: C('Link/Light/Special'),
                      textAlign: 'center',
                    },
                  ]}
                >
                  {i18n.t('Keep attachment')}
                </Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    </ModalWrapper>
  );
};

export default ColorWrapper(RemoveAttachmentModal);
